import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { closeModal, SweetAlertShow, LoadingDiv } from "../master/Master";
import { Link } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import {
  messEnrollDelete,
  messEnrollList,
  messEnrollStatusChange,
  messEnrollUpdate,
  messList,
} from "../../api/Mess";
import Swal from "sweetalert2";
import { formatToDate } from "../master/FormatToDateTime";
import MessLayout from "./MessLayout";

export default function MessEnrollList() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [messes, setMesses] = useState([]);
  const [data, setData] = useState({});
  const [filters, setFilters] = useState({ page: 1 });
  const [modalData, setModalData] = useState({});

  const enrollments = data?.data || [];

  /* -------------------------------------------------------
     Fetch Mess List
  ------------------------------------------------------- */
  const getMessList = async () => {
    setLoading(true);

    try {
      const response = await messList(`&limit=all`);

      if (response.data?.status) {
        setMesses(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     Fetch Enrollment List
  ------------------------------------------------------- */
  const getDataList = useCallback(async () => {
    setLoading(true);

    let addUrl = "";

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value !== undefined && value !== null && value !== "") {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    try {
      const response = await messEnrollList(addUrl);

      if (response.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    getMessList();
  }, []);

  useEffect(() => {
    getDataList();
  }, [getDataList]);

  /* -------------------------------------------------------
     Statistics
  ------------------------------------------------------- */
  const statistics = useMemo(() => {
    const active = enrollments.filter(
      (item) => String(item?.status) === "1",
    ).length;

    const inactive = enrollments.filter(
      (item) => String(item?.status) !== "1",
    ).length;

    const students = enrollments.filter(
      (item) => item?.type === "Student",
    ).length;

    const staff = enrollments.filter((item) => item?.type === "Staff").length;

    const guests = enrollments.filter((item) => item?.type === "Guest").length;

    return {
      total: enrollments.length,
      active,
      inactive,
      students,
      staff,
      guests,
    };
  }, [enrollments]);

  /* -------------------------------------------------------
     Update Enrollment
  ------------------------------------------------------- */
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await messEnrollUpdate(formData);

      if (response?.data?.status) {
        closeModal("editEnroll");

        SweetAlertShow("success", "Successfully", response?.data?.message);

        getDataList();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     Change Status
  ------------------------------------------------------- */
  const handleStatusChange = async (id, end_date) => {
    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("id", id);
      formData.append("end_date", end_date);

      const response = await messEnrollStatusChange(formData);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getDataList();
    }
  };

  /* -------------------------------------------------------
     Delete Enrollment
  ------------------------------------------------------- */
  const deleteEnroll = async (id) => {
    setLoading(true);

    try {
      const response = await messEnrollDelete(id);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getDataList();
    }
  };

  /* -------------------------------------------------------
     Status Click
  ------------------------------------------------------- */
  const handleStatusClick = async (item) => {
    if (String(item?.status) !== "1") return;

    const today = new Date().toISOString().split("T")[0];

    const { value: end_date } = await Swal.fire({
      title: "Mark Enrollment Inactive",
      text: "Select the last date of the mess enrollment.",
      icon: "warning",
      input: "date",
      inputLabel: "End Date",
      inputValue: today,
      showCancelButton: true,
      confirmButtonText: "Mark Inactive",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value) {
          return "Please select an end date.";
        }
      },
    });

    if (end_date) {
      handleStatusChange(item?.id, end_date);
    }
  };

  /* -------------------------------------------------------
     Helpers
  ------------------------------------------------------- */
  const getInitials = (name) => {
    if (!name) return "?";

    return name
      .trim()
      .split(" ")
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const getTypeClass = (type) => {
    switch (type) {
      case "Student":
        return "bg-label-primary";

      case "Staff":
        return "bg-label-info";

      case "Guest":
        return "bg-label-warning";

      default:
        return "bg-label-secondary";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "Student":
        return "ri-graduation-cap-line";

      case "Staff":
        return "ri-user-line";

      case "Guest":
        return "ri-user-star-line";

      default:
        return "ri-user-line";
    }
  };

  const resetFilters = () => {
    setFilters({
      page: 1,
    });
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row py-5">
                  <MessLayout />

                  <div className="col-12">
                    {/* ==================================================
                        PAGE HEADER
                    ================================================== */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body py-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                              style={{
                                width: "48px",
                                height: "48px",
                              }}
                            >
                              <i className="ri-restaurant-2-line ri-24px"></i>
                            </div>

                            <div>
                              <h4 className="mb-1">Mess Enrollments</h4>

                              <p className="text-muted mb-0 small">
                                Manage student, staff and guest mess enrollments
                              </p>
                            </div>
                          </div>

                          {hasAccess("/mess/enroll") && (
                            <Link to="/mess/enroll" className="btn btn-primary">
                              <i className="ri-add-line me-1"></i>
                              New Enrollment
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        STATISTICS
                    ================================================== */}
                    <div className="row g-3 mb-4">
                      {/* Total */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <p className="text-muted mb-1">
                                  Total Enrollments
                                </p>

                                <h3 className="mb-1">{statistics.total}</h3>

                                <small className="text-muted">
                                  Current filtered records
                                </small>
                              </div>

                              <div className="avatar">
                                <span className="avatar-initial rounded bg-label-primary">
                                  <i className="ri-group-line ri-22px"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Active */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <p className="text-muted mb-1">Active</p>

                                <h3 className="mb-1 text-success">
                                  {statistics.active}
                                </h3>

                                <small className="text-muted">
                                  Currently enrolled
                                </small>
                              </div>

                              <div className="avatar">
                                <span className="avatar-initial rounded bg-label-success">
                                  <i className="ri-checkbox-circle-line ri-22px"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Students */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <p className="text-muted mb-1">Students</p>

                                <h3 className="mb-1">{statistics.students}</h3>

                                <small className="text-muted">
                                  Student enrollments
                                </small>
                              </div>

                              <div className="avatar">
                                <span className="avatar-initial rounded bg-label-info">
                                  <i className="ri-graduation-cap-line ri-22px"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Staff / Guest */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <p className="text-muted mb-1">
                                  Staff / Guests
                                </p>

                                <h3 className="mb-1">
                                  {statistics.staff + statistics.guests}
                                </h3>

                                <small className="text-muted">
                                  {statistics.staff} staff · {statistics.guests}{" "}
                                  guests
                                </small>
                              </div>

                              <div className="avatar">
                                <span className="avatar-initial rounded bg-label-warning">
                                  <i className="ri-user-star-line ri-22px"></i>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        FILTERS
                    ================================================== */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header border-bottom py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <i className="ri-filter-3-line text-primary ri-20px"></i>

                            <h5 className="mb-0">Filters</h5>
                          </div>

                          <button
                            type="button"
                            onClick={resetFilters}
                            className="btn btn-sm btn-label-danger"
                          >
                            <i className="ri-refresh-line me-1"></i>
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="row g-3">
                          {/* Mess */}
                          <div className="col-xl-3 col-md-6">
                            <label className="form-label">
                              <i className="ri-restaurant-line me-1"></i>
                              Mess
                            </label>

                            <select
                              name="mess_id"
                              id="mess_id"
                              className="form-select"
                              value={filters?.mess_id || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  page: 1,
                                  mess_id: e.target.value,
                                })
                              }
                            >
                              <option value="">All Messes</option>

                              {messes?.map((mess) => (
                                <option value={mess?.id} key={mess?.id}>
                                  {mess?.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Type */}
                          <div className="col-xl-3 col-md-6">
                            <label className="form-label">
                              <i className="ri-user-settings-line me-1"></i>
                              Enrollment Type
                            </label>

                            <select
                              name="type"
                              id="type"
                              className="form-select"
                              value={filters?.type || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  page: 1,
                                  type: e.target.value,
                                })
                              }
                            >
                              <option value="">All Types</option>

                              <option value="Student">Student</option>

                              <option value="Staff">Staff</option>

                              <option value="Guest">Guest</option>
                            </select>
                          </div>

                          {/* Search */}
                          <div className="col-xl-5 col-md-8">
                            <label className="form-label">
                              <i className="ri-search-line me-1"></i>
                              Search
                            </label>

                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="ri-search-line"></i>
                              </span>

                              <input
                                type="text"
                                className="form-control"
                                name="search"
                                placeholder="Search by name, mobile, father name..."
                                value={filters?.search || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    page: 1,
                                    search: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* Result */}
                          <div className="col-xl-1 col-md-4 d-flex align-items-end">
                            <div className="text-center w-100">
                              <div className="text-muted small">Results</div>

                              <h5 className="mb-0">{statistics.total}</h5>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        TABLE
                    ================================================== */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header border-bottom py-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                          <div>
                            <h5 className="mb-1">Enrollment List</h5>

                            <small className="text-muted">
                              Showing <strong>{statistics.total}</strong>{" "}
                              enrollment
                              {statistics.total !== 1 ? "s" : ""}
                            </small>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-label-success px-3 py-2">
                              <i className="ri-checkbox-circle-line me-1"></i>
                              {statistics.active} Active
                            </span>

                            <span className="badge bg-label-danger px-3 py-2">
                              <i className="ri-close-circle-line me-1"></i>
                              {statistics.inactive} Inactive
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead>
                              <tr className="text-nowrap">
                                <th className="ps-4">#</th>

                                <th>Member</th>

                                <th>Type</th>

                                <th>Contact</th>

                                <th>Address</th>

                                <th>Mess</th>

                                <th>Joining</th>

                                <th>End Date</th>

                                <th className="text-center">Status</th>

                                <th className="text-center pe-4">Actions</th>
                              </tr>
                            </thead>

                            <tbody>
                              {enrollments?.length > 0 ? (
                                enrollments.map((item, i) => (
                                  <tr key={item?.id}>
                                    {/* Number */}
                                    <td className="ps-4 text-muted">{i + 1}</td>

                                    {/* Member */}
                                    <td>
                                      <div className="d-flex align-items-center gap-3">
                                        <div className="avatar flex-shrink-0">
                                          <span className="avatar-initial rounded-circle bg-label-primary">
                                            {getInitials(item?.name)}
                                          </span>
                                        </div>

                                        <div>
                                          <h6 className="mb-1 text-nowrap">
                                            {item?.name || "-"}
                                          </h6>

                                          {item?.type === "Student" &&
                                            item?.father_name && (
                                              <small className="text-muted d-block">
                                                <i className="ri-parent-line me-1"></i>
                                                {item?.father_name}
                                              </small>
                                            )}

                                          {item?.type === "Guest" && (
                                            <small className="text-muted">
                                              Guest Enrollment
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* Type */}
                                    <td>
                                      <span
                                        className={`badge ${getTypeClass(
                                          item?.type,
                                        )}`}
                                      >
                                        <i
                                          className={`${getTypeIcon(
                                            item?.type,
                                          )} me-1`}
                                        ></i>

                                        {item?.type || "-"}
                                      </span>
                                    </td>

                                    {/* Contact */}
                                    <td>
                                      {item?.mobile ? (
                                        <a
                                          href={`tel:${item?.mobile}`}
                                          className="text-body text-decoration-none"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="avatar avatar-xs">
                                              <span className="avatar-initial rounded bg-label-success">
                                                <i className="ri-phone-line"></i>
                                              </span>
                                            </span>

                                            <span className="text-nowrap">
                                              {item?.mobile}
                                            </span>
                                          </div>
                                        </a>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>

                                    {/* Address */}
                                    <td style={{ minWidth: "180px" }}>
                                      <div
                                        className="text-muted"
                                        style={{
                                          maxWidth: "220px",
                                        }}
                                      >
                                        {item?.address ? (
                                          <span title={item?.address}>
                                            <i className="ri-map-pin-line me-1 text-danger"></i>

                                            {item?.address.length > 40
                                              ? `${item?.address.substring(
                                                  0,
                                                  40,
                                                )}...`
                                              : item?.address}
                                          </span>
                                        ) : (
                                          "-"
                                        )}
                                      </div>
                                    </td>

                                    {/* Mess */}
                                    <td>
                                      <span className="d-inline-flex align-items-center gap-1 text-nowrap">
                                        <i className="ri-restaurant-2-line text-primary"></i>

                                        {item?.mess?.name || "-"}
                                      </span>
                                    </td>

                                    {/* Joining */}
                                    <td className="text-nowrap">
                                      <div>
                                        <span className="fw-medium">
                                          {formatToDate(
                                            item?.joining_date ??
                                              item?.created_at,
                                          )}
                                        </span>
                                      </div>
                                    </td>

                                    {/* End Date */}
                                    <td className="text-nowrap">
                                      {item?.last_date ? (
                                        <span className="text-danger">
                                          {formatToDate(item?.last_date)}
                                        </span>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>

                                    {/* Status */}
                                    <td className="text-center">
                                      {hasAccess("/mess/status") ? (
                                        <button
                                          type="button"
                                          className={`btn btn-sm rounded-pill px-3 ${
                                            String(item?.status) === "1"
                                              ? "btn-label-success"
                                              : "btn-label-danger"
                                          }`}
                                          disabled={
                                            String(item?.status) === "2"
                                          }
                                          onClick={() =>
                                            handleStatusClick(item)
                                          }
                                        >
                                          <i
                                            className={
                                              String(item?.status) === "1"
                                                ? "ri-checkbox-circle-line me-1"
                                                : "ri-close-circle-line me-1"
                                            }
                                          ></i>

                                          {String(item?.status) === "1"
                                            ? "Active"
                                            : "Inactive"}
                                        </button>
                                      ) : (
                                        <span
                                          className={`badge ${
                                            String(item?.status) === "1"
                                              ? "bg-label-success"
                                              : "bg-label-danger"
                                          }`}
                                        >
                                          {String(item?.status) === "1"
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                      )}
                                    </td>

                                    {/* Actions */}
                                    <td className="text-center pe-4">
                                      <div className="d-flex align-items-center justify-content-center gap-1">
                                        {/* Edit */}
                                        {hasAccess("/mess/enroll/edit") && (
                                          <button
                                            type="button"
                                            onClick={() => setModalData(item)}
                                            className="btn btn-sm btn-icon btn-label-info"
                                            data-bs-toggle="modal"
                                            data-bs-target="#editEnroll"
                                            title="Edit Enrollment"
                                          >
                                            <i className="ri-edit-line"></i>
                                          </button>
                                        )}

                                        {/* Guest Card */}
                                        {item?.type === "Guest" && (
                                          <Link
                                            to={`/mess/guest/card?id=${item?.id}`}
                                            className="btn btn-sm btn-icon btn-label-primary"
                                            title="Guest Card"
                                          >
                                            <i className="ri-id-card-line"></i>
                                          </Link>
                                        )}

                                        {/* Delete */}
                                        {hasAccess("/mess/enroll/delete") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-label-danger"
                                            title="Delete Enrollment"
                                            onClick={() =>
                                              Swal.fire({
                                                title: "Delete Enrollment?",
                                                text: "This action will permanently delete the enrollment.",
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#d33",
                                                cancelButtonColor: "#6c757d",
                                                confirmButtonText:
                                                  "Yes, Delete",
                                                cancelButtonText: "Cancel",
                                              }).then((result) => {
                                                if (result.isConfirmed) {
                                                  deleteEnroll(item?.id);
                                                }
                                              })
                                            }
                                          >
                                            <i className="ri-delete-bin-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                /* Empty State */
                                <tr>
                                  <td colSpan="10" className="text-center py-5">
                                    <div className="py-4">
                                      <div
                                        className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center bg-label-primary"
                                        style={{
                                          width: "70px",
                                          height: "70px",
                                        }}
                                      >
                                        <i className="ri-restaurant-line ri-32px"></i>
                                      </div>

                                      <h5 className="mb-2">
                                        No enrollments found
                                      </h5>

                                      <p className="text-muted mb-3">
                                        No mess enrollment records match the
                                        selected filters.
                                      </p>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-label-primary"
                                        onClick={resetFilters}
                                      >
                                        <i className="ri-refresh-line me-1"></i>
                                        Clear Filters
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          EDIT ENROLLMENT MODAL
      ============================================================ */}
      <div
        className="modal fade"
        id="editEnroll"
        tabIndex={-1}
        aria-labelledby="editEnrollLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <form onSubmit={handleUpdate}>
              {/* Modal Header */}
              <div className="modal-header border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                    style={{
                      width: "44px",
                      height: "44px",
                    }}
                  >
                    <i className="ri-edit-2-line ri-22px"></i>
                  </div>

                  <div>
                    <h5 className="modal-title mb-1" id="editEnrollLabel">
                      Edit Enrollment
                    </h5>

                    <small className="text-muted">
                      Update mess enrollment information
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              {/* Modal Body */}
              <div className="modal-body">
                <input type="hidden" name="id" value={modalData?.id || ""} />

                <input
                  type="hidden"
                  name="type"
                  value={modalData?.type || ""}
                />

                <input
                  type="hidden"
                  name="mess_id"
                  value={modalData?.mess_id || ""}
                />

                {/* Member Summary */}
                <div className="p-3 rounded bg-label-primary mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                      style={{
                        width: "48px",
                        height: "48px",
                      }}
                    >
                      {getInitials(modalData?.name)}
                    </div>

                    <div>
                      <h6 className="mb-1">
                        {modalData?.name || "Enrollment"}
                      </h6>

                      <small>{modalData?.type || "-"} Enrollment</small>
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  {/* Name */}
                  <div className="col-md-6">
                    <label className="form-label">Name</label>

                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="ri-user-line"></i>
                      </span>

                      <input
                        type="text"
                        className="form-control input-uppercase"
                        placeholder="Enter name"
                        name="name"
                        defaultValue={modalData?.name || ""}
                      />
                    </div>
                  </div>

                  {/* Father Name */}
                  <div className="col-md-6">
                    <label className="form-label">Father Name</label>

                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="ri-parent-line"></i>
                      </span>

                      <input
                        type="text"
                        className="form-control input-uppercase"
                        placeholder="Enter father name"
                        name="father_name"
                        defaultValue={modalData?.father_name || ""}
                      />
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="col-md-6">
                    <label className="form-label">Contact Number</label>

                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="ri-phone-line"></i>
                      </span>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter contact number"
                        name="mobile"
                        defaultValue={modalData?.mobile || ""}
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-md-6">
                    <label className="form-label">Address</label>

                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="ri-map-pin-line"></i>
                      </span>

                      <input
                        type="text"
                        className="form-control input-uppercase"
                        placeholder="Enter address"
                        name="address"
                        defaultValue={modalData?.address || ""}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1"></i>
                      Update Enrollment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
